import React from 'react';
import { formatCurrency } from '../utils/formatting';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

export const OrderSummaryStep = ({
  customerData,
  paidItems,
  giftItems,
  getFormattedCartTotal,
  getFormattedSubtotal,
  selectedCurrency,
  getCartTotal,
  getDiscountedCartTotal,
  discountAmt,
  checkoutPaymentPeriod,
  installmentOverride,
  numberOfInstallments,
  getSelectedPaymentPeriod,
  orderPath,
  setOrderPath,
  loading,
  setStep,
  createOrder,
  demoData
}) => {
  const currentPeriod = giftItems.length > 0 ? getSelectedPaymentPeriod(giftItems) : checkoutPaymentPeriod;
  const periodDaysMap = { 'day1': 1, 'day30': 30, 'day60': 60, 'day90': 90, 'day180': 180 };
  const periodDays = periodDaysMap[currentPeriod] || 1;
  const finalAmount = installmentOverride || Math.round(getDiscountedCartTotal() / parseInt(numberOfInstallments || '1')).toFixed(0);
  const finalDate = (() => {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + periodDays);
    return futureDate.toISOString().slice(0, 10);
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7FBFC] to-[#E8F4F8] pt-14">
      <div className="container mx-auto px-4 py-4">
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2 text-[#011841]">Order Summary</h2>
          <p className="text-[#033EA5]">Review your order before submitting</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4 text-[#011841]">Customer Information</h2>
          <div className="space-y-4">
            {customerData.demoBookingId && (
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Associated Demo Booking</h3>
                <div className="bg-gray-50 p-3 rounded-md">
                  <div>
                    <p className="font-medium">
                      {demoData?.customer?.name || customerData.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      Demo ID: {demoData?.demoId || customerData.demoBookingId}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium">Name: <span className="font-normal text-gray-700">{customerData.name}</span></p>
              </div>
              <div>
                <p className="font-medium">Phone: <span className="font-normal text-gray-700">{customerData.phone}</span></p>
                {customerData.email && (
                  <p className="font-medium mt-1">Email: <span className="font-normal text-gray-700">{customerData.email}</span></p>
                )}
                {customerData.paymentMethod === 'payment_link' && (
                  <p className="font-medium mt-1">
                    Payment Link: 
                    <a href={customerData.paymentLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-1">
                      View Payment
                    </a>
                  </p>
                )}
              </div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium">Billing Address:</span>
                  <p className="mt-1 text-gray-700">{customerData.billingAddress || 'Not provided'}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Shipping Address:</span>
                    {customerData.sameAsBilling && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Same as billing</span>
                    )}
                  </div>
                  <p className="mt-1 text-gray-700">
                    {customerData.sameAsBilling ? customerData.billingAddress : (customerData.shippingAddress || 'Not provided')}
                  </p>
                </div>
              </div>
            </div>
            
            {customerData.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <span className="font-medium text-gray-700">Notes:</span> 
                <p className="text-gray-600 text-sm mt-1">{customerData.notes}</p>
              </div>
            )}
            
            {/* Upgrade Information */}
            {paidItems.some(item => item.isUpgrade) && (
              <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-purple-900 flex items-center">
                  <span className="mr-2">✨</span> Set Upgrade Summary
                </h3>
                {paidItems.filter(item => item.isUpgrade).map((item, idx) => (
                  <div key={`upgrade-sum-${idx}`} className="text-sm text-purple-800 space-y-1">
                    <p><strong>Applying Upgrade to:</strong> {item.name}</p>
                    <p><strong>Upgrading from:</strong> {item.upgradeFrom} (Order #{item.upgradeFromOrder})</p>
                    <p className="text-xs text-purple-600 mt-1 italic">This order will be linked to the original purchase for audit tracking.</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Items */}
          {paidItems.length > 0 && (
            <div className="card mb-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Paid Items ({paidItems.length})</h3>
              <div className="space-y-3">
                {paidItems.map((item, index) => (
                  <div key={`paid-${index}`} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-600">Qty: {item.quantity}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(item.price * item.quantity, selectedCurrency)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {giftItems.length > 0 && (
            <div className="card mb-6">
              <h3 className="text-lg font-semibold mb-4">Gift Items ({giftItems.length})</h3>
              <div className="space-y-3">
                {giftItems.map((item, index) => (
                  <div key={`gift-${index}`} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-600">Qty: {item.quantity} • Gift Item</div>
                    </div>
                    <div className="text-right">
                      {/* No value shown for gifts */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Plan Details */}
          {currentPeriod !== 'day1' ? (
            <div className="card mb-6 bg-blue-50 border-2 border-blue-200">
              <h3 className="text-lg font-semibold mb-4 text-blue-800">
                💳 Payment Plan Details
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-blue-700">Period (Days):</span>
                    <p className="text-gray-800">{periodDays} Days</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Number of Installments:</span>
                    <p className="text-gray-800">{numberOfInstallments}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Installment Amount:</span>
                    <p className="text-gray-800 font-semibold">
                      {selectedCurrency} {finalAmount}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Final Payment Date:</span>
                    <p className="text-gray-800">{finalDate}</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Important:</strong> Gift items will be delivered upon completion of all payments as specified in payment plan.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="card mb-6 bg-green-50 border-2 border-green-200">
              <h3 className="text-lg font-semibold mb-2 text-green-800">
                ✅ Payment Information
              </h3>
              <p className="text-green-700">Full payment due immediately</p>
            </div>
          )}

          {/* Order Type Selection */}
          <div className="card mb-6 p-6 border border-dashed border-[#19BFB7]/40 bg-[#F7FBFC]">
            <h3 className="text-lg font-semibold mb-4">Order Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`border rounded-lg p-4 cursor-pointer ${orderPath === 'immediate' ? 'border-[#19BFB7] bg-white shadow-sm' : 'border-gray-200 bg-[#f7fbfc]'}`}>
                <input
                  type="radio"
                  name="orderPath"
                  value="immediate"
                  checked={orderPath === 'immediate'}
                  onChange={() => setOrderPath('immediate')}
                  className="mr-3"
                />
                Create Order Now
                <p className="text-sm text-gray-600 mt-2">Immediate confirmed order with OrderNo and direct processing.</p>
              </label>
              <label className={`border rounded-lg p-4 cursor-pointer ${orderPath === 'draft' ? 'border-[#19BFB7] bg-white shadow-sm' : 'border-gray-200 bg-[#f7fbfc]'}`}>
                <input
                  type="radio"
                  name="orderPath"
                  value="draft"
                  checked={orderPath === 'draft'}
                  onChange={() => setOrderPath('draft')}
                  className="mr-3"
                />
                Save Draft for Customer
                <p className="text-sm text-gray-600 mt-2">Create a draft order and send a secure customer link for review and confirmation.</p>
              </label>
            </div>
          </div>

          {/* Order Total */}
          <div className="card mb-6">
            {discountAmt > 0 ? (
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{getFormattedSubtotal()}</span>
                </div>
                <div className="flex justify-between text-red-600 font-medium">
                  <span>Discount:</span>
                  <span>-{formatCurrency(discountAmt, selectedCurrency)}</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#011841] pt-2 border-t border-gray-100">
                  <span>Total Amount:</span>
                  <span>{getFormattedCartTotal()}</span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total Amount:</span>
                <span>{getFormattedCartTotal()}</span>
              </div>
            )}
            <div className="text-sm text-gray-600 mt-2">
              {paidItems.length} paid items • {giftItems.length} gift items
            </div>
          </div>

          {/* Navigation */}
          <div className="flex space-x-4 mt-6">
            <button
              type="button"
              onClick={() => setStep('customer')}
              className="bg-[#F7FBFC] hover:bg-[#E8F4F8] text-[#022b73] px-6 py-3 rounded-lg font-medium transition-all duration-300 border-2 border-[#19BFB7]/30 hover:border-[#19BFB7] flex-1"
            >
              Edit Details
            </button>
            <button
              type="button"
              onClick={createOrder}
              disabled={loading}
              className="bg-gradient-to-r from-[#19BFB7] to-[#28E3DA] hover:from-[#17a8a0] hover:to-[#25d1c8] text-[#011841] px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex-1 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" /> : orderPath === 'draft' ? 'Save Draft' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
